import { Location } from '@angular/common';
import { Router } from '@angular/router';
import moment from 'moment-timezone';
import Papa from 'papaparse/papaparse.min.js';
import { PubShareType, PublisherShare } from '../domain/publisher.model';
import { SummedMasterReport } from '../domain/rb/summed-master-report.model';
import { SortUtils } from './sort-utils';
import { COMMON_TWO_PART_TLDS } from './domain-constants';
export class Utils {
  private router: Router;

  public static generateGuid() {
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  }

  public static isNullOrUndefined(value) {
    return value === undefined || value === null;
  }

  public static isNullUndefOrEmpty(value) {
    return value === undefined || value === null || value.toString().trim() === '';
  }

  public static randomInt() {
    return Math.random();
  }

  public static isMobile() {
    return /Android|iPhone/i.test(window.navigator.userAgent);
  }

  public static sortComparatorByAttribute(key: string) {
    return (a, b) => {
      if (a[key] < b[key]) {
        return -1;
      }
      if (a[key] > b[key]) {
        return 1;
      }
      return 0;
    };
  }

  public static exportCsv(csvData, csvExportName?, headerData?) {
    const csv = csvData ? Papa.unparse(csvData) : null;
    const header = headerData ? Papa.unparse(headerData) : null;
    if (csv) {
      const blobParts = [header || '', csv];
      const dateString = new Date().toISOString();
      csvExportName = csvExportName ? `${csvExportName}-${dateString}.csv` : `csv-export-${dateString}.csv`;
      Utils.exportBlob(blobParts, csvExportName);
    }
  }

  public static exportBlobCsv(csv, name?) {
    name = name || `csv-export-${new Date().toISOString()}.csv`;
    Utils.exportBlob([csv], name);
  }

  public static exportBlob(blobParts: any[], name?, type = 'text/csv') {
    const blob = new Blob(blobParts, { type });

    if (!name) {
      const dateString = new Date().toISOString();
      name = 'export-' + dateString + '.csv';
    }
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    setTimeout(() => {
      a.click();
      document.body.removeChild(a);
    }, 0);
  }

  /**
   * @param obj
   * @param key like 'publisher.widget.type'
   * @return value of object.publisher.widget.type
   */
  static getNestedValue(obj, key) {
    if (key) {
      return key.split('.').reduce((result, key2) => (result ? result[key2] : null), obj);
    }
  }

  static getUrlParamsFrom(obj) {
    return Object.keys(obj).reduce((seed, current) => {
      if (seed) {
        seed += '&';
      }
      if (obj[current]) {
        seed += `${current}=${obj[current]}`;
      }
      return seed;
    }, '');
  }

  static pct(a: number, base: number) {
    return (a * 100) / (base || 1);
  }

  // static generateAdUnitTag(adUnit: AdUnit) {
  //   return `<script type="text/javascript" id="${adUnit.widgetId}" src="${environment.cdnUrl}/${adUnit.widgetId}/index.js?cb=%%CACHEBUSTER%%" charset="utf-8" async></script>`;
  // }

  public static convertDateLocaleToUtc(date: string) {
    const stillUtc = moment(date).utc();
    return moment(stillUtc).format('YYYY-MM-DDTHH:mm:ss');
  }

  public static convertDateToUtcFromTimezone(date: string, timezone: string) {
    timezone = timezone === 'EST' ? 'America/New_York' : timezone;
    const UtcFormatted = moment.tz(date, 'YYYY-MM-DDTHH:mm:ss', timezone).utc().format('YYYY-MM-DDTHH:mm:ss');
    return UtcFormatted;
  }

  public static convertDateUtcToTimezone(date: string, timezone: string) {
    timezone = timezone === 'EST' ? 'America/New_York' : timezone;
    const timezoneFormatted = moment.utc(date, 'YYYY-MM-DDTHH:mm:ss').tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
    return timezoneFormatted;
  }

  public static convertDateUTCToLocal(date: string) {
    return moment(moment.utc(date).toDate()).local().format('YYYY-MM-DDTHH:mm:ss');
  }

  public static getTimeFromSeconds(durationInSeconds) {
    return durationInSeconds ? this.fancyTimeFormat(durationInSeconds) : '00:00';
  }

  public static fancyTimeFormat(time) {
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    let ret = '';

    if (hrs > 0) {
      ret += '' + hrs + ':';
    }
    ret += '' + (mins < 10 ? '0' + mins : mins) + ':' + (secs < 10 ? '0' : '');
    ret += '' + secs;
    return ret;
  }

  public static parseListOfDomains(domains: string): string[] {
    return Array.from(new Set(domains?.toLowerCase().replace(/;/g, ',').replace(/\n/g, ',').replace(/\t/g, ',').replace(/ /, ',').split(','))).filter((d) => d);
  }

  public static getRevenueValue(shares: PublisherShare[], type: PubShareType): number {
    if (shares && shares.length) {
      const filteredVales = shares.filter((item) => item.pubShareType === type);
      if (filteredVales.length) {
        filteredVales.sort(SortUtils.propertyComparatorString('day', true));
        return filteredVales[0].value;
      }
    }
    return;
  }

  public static cutLongText(value: string, limit = 140, completeWords = true, ellipsis = ' ...') {
    if (completeWords) {
      limit = value.substr(0, limit).lastIndexOf(' ');
    }
    return value.length > limit ? value.substr(0, limit) + (ellipsis ? ellipsis : '') : value;
  }

  public static formatBites(bytes, decimals = 0): string {
    if (bytes === 0) {
      return '0 b';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  public static getEmailValidationPattern() {
    // eslint-disable-next-line max-len
    return /(?:(?:\r\n)?[ \t])*(?:(?:(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*)|(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*:(?:(?:\r\n)?[ \t])*(?:(?:(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*)(?:,\s*(?:(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \000-\031]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*))*)?;\s*)/;
  }

  public static getDomainValidationPattern() {
    return /^(?:(?:https?|ftp|smtp):\/\/)?(?:[a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,}.*$/;
  }

  // Normalizes a domain input by extracting the registrable domain.
  // Removes protocol, www prefix, paths, query strings, fragments, and ports.
  // @param input - The domain input to normalize (can be URL or domain).
  // @returns The normalized registrable domain.
  // Example:
  // normalizeDomain('https://www.hulu.com/start?cmp=123') -> 'hulu.com'
  // normalizeDomain('www.example.com') -> 'example.com'
  // normalizeDomain('subdomain.example.com:8080/path') -> 'example.com'
  public static normalizeDomain(input: string): string {
    if (!input || typeof input !== 'string') {
      return '';
    }

    let domain = input.trim().toLowerCase();

    // Remove protocol (http://, https://, ftp://, etc.)
    domain = domain.replace(/^[a-z]+:\/\//i, '');

    // Remove www prefix
    domain = domain.replace(/^www\./i, '');

    // Remove path, query string, and fragment (everything after first /, ?, or #)
    domain = domain.split(/[\/\?#]/)[0];

    // Remove port number
    domain = domain.replace(/:[0-9]+$/, '');

    // Extract registrable domain (remove subdomains except for the main domain)
    // This is a simplified approach - for more complex cases, you might want to use a public suffix list
    const parts = domain.split('.');
    if (parts.length >= 2) {
      // Keep only the last two parts (domain + TLD) for most cases
      // This handles cases like subdomain.example.com -> example.com
      // But keeps cases like example.co.uk as example.co.uk
      const lastTwo = parts.slice(-2).join('.');

      // Handle common two-part TLDs (co.uk, com.au, etc.)
      const lastThree = parts.slice(-3).join('.');

      if (parts.length >= 3 && COMMON_TWO_PART_TLDS.some((tld) => lastThree.endsWith(tld))) {
        domain = lastThree;
      } else {
        domain = lastTwo;
      }
    }

    return domain;
  }

  // public static getLivePreviewDomainValidationPattern() {
  //   return /^https?:\/\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&\/=]*)$/;
  // }

  public static getHexColorValidationPattern() {
    return /^#(?:[0-9a-fA-F]{6})$/;
  }

  public static getUnique(arr, comp) {
    // store the comparison  values in array
    const unique = arr
      .map((e) => e[comp])

      // store the indexes of the unique objects
      .map((e, i, final) => final.indexOf(e) === i && i)

      // eliminate the false indexes & return unique objects
      .filter((e) => arr[e])
      .map((e) => arr[e]);
    return unique;
  }

  public static assignParamsToURL(router: Router, location: Location, filter: any) {
    const urlTree = router.createUrlTree([], {
      queryParams: Object.assign({}, filter),
      queryParamsHandling: 'merge',
      preserveFragment: true
    });
    location.replaceState(urlTree.toString());
  }

  public static getRangeOfNumbers(start: number, end: any): number[] {
    const range = Array.from(Array(end - start + 1).keys()).map((x) => x + start);
    return range;
  }

  /** If startDate == endDate return true, else false. */
  public static checkDates(data: SummedMasterReport[]): boolean {
    if (data.length > 0) {
      if (data[0].day == data[data.length - 1].day) {
        return true;
      } else {
        false;
      }
    } else {
      false;
    }
  }

  public static removeEmptyFromJson(obj) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        const childObject = Utils.removeEmptyFromJson(obj[key]);
        if (childObject === undefined) {
          delete obj[key];
        }
      } else if (obj[key] === '' || obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      }
      if (Array.isArray(obj[key]) && !obj[key][0]) {
        delete obj[key];
      }
    });
    return Object.keys(obj).length > 0 || obj instanceof Array ? obj : undefined;
  }

  /** Removes object from list.  Pass list and object name. */
  public static removeFromList(list, objectName: string) {
    list.splice(list.indexOf(objectName), 1);
  }

  public static getInitialsFromString(data: string = ''): string {
    const rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    const initials = [...data.matchAll(rgx)] || [];
    return ((initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')).toUpperCase();
  }

  public static removeHtmlTagsFromString(data: string = ''): string {
    return data?.replace(/<[^>]*>/g, '');
  }

  public static removeWhitespace(data: string) {
    return data.replace(/ /g, '');
  }

  public static scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  public static enumToArray = <T>(enumObj: T): string[] => Object.values(enumObj).filter((value) => typeof value === 'string') as string[];

  public static logInvalidControls(formGroup: any): void {
    const findInvalidControls = (form: any, path: string = ''): void => {
      Object.keys(form.controls).forEach((key) => {
        const control = form.controls[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (control.invalid) {
          console.log(`Invalid control at: ${currentPath}`);
        }

        if (control.controls) {
          // If it's a FormGroup or FormArray, recurse
          findInvalidControls(control, currentPath);
        }
      });
    };

    findInvalidControls(formGroup);
  }
}
